import { smartService } from '../xstate/smart.xstate';

/**
 * Returns the logged-in user's favorite org ids ("My Orgs"), or an empty array
 * when none are set yet (every existing user before this field existed, and
 * every brand-new user).
 */
export function getFavoriteOrgIds(): string[] {
  return smartService.state.context?.userInfo?.settings?.favoriteOrgIds || [];
}

/**
 * The id of the logged-in user smartService's `userInfo` describes, or
 * `undefined` before the profile has loaded.
 *
 * A consumer that independently persists `settings` (e.g. a profile page's
 * own Save button, saving a snapshot it loaded through its own query) needs
 * this to tell whether the page it is saving IS the logged-in user before
 * merging smartService's own `favoriteOrgIds` into that snapshot — merging
 * it into another user's settings would be meaningless (favorites belong to
 * the viewer, not the viewed profile).
 */
export function getFavoriteOrgsUserId(): string | undefined {
  return smartService.state.context?.userInfo?.id;
}

/**
 * Resolves once smartService's `userInfo` sub-machine is no longer mid-write
 * (`configured.userInfo.updating`) — the parallel region `toggleFavoriteOrg`
 * reads from. Reading `smartService.state.context` instead, from inside the
 * `updateUser` event's own `callback`, would still see the PRE-write value:
 * `callback` runs synchronously inside the `assign` action that computes the
 * patch, before the interpreter commits it (verified against the real
 * `xstate` package — `scratchpad/xstate-race-repro/repro.mjs`, PSTUDIO-3330
 * review round 2). A `subscribe` callback fires after the commit, so this is
 * the one place in this module allowed to look at `.context` from inside it.
 */
function waitForUserInfoIdle(): Promise<void> {
  if (!smartService.state.matches('configured.userInfo.updating')) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const subscription = smartService.subscribe((state) => {
      if (!state.matches('configured.userInfo.updating')) {
        subscription.unsubscribe();
        resolve();
      }
    });
  });
}

// Every `toggleFavoriteOrg` call is chained onto this, so a click is never
// computed against a stale `favoriteOrgIds` snapshot (see the function's own
// docblock for the failure this prevents).
let pendingToggle: Promise<void> = Promise.resolve();

/**
 * Adds or removes `orgId` from the logged-in user's favorite orgs and persists
 * the change immediately through smartService's existing `updateUser` event —
 * the same mutation every other profile/settings field already uses, merging
 * the new list into `settings` and keeping every other required field
 * (`id`/`accountId`/`firstName`/`lastName`) unchanged.
 *
 * The ONE function both the Org nav menu (`universal-nav-org-selector.wc.ts`)
 * and any app-level consumer (e.g. a user's roles table) call, so a star
 * toggled in either place is instantly consistent with the other — no reload,
 * no second, divergent write path.
 *
 * Calls are serialized through `pendingToggle` (PSTUDIO-3330 review round 2,
 * confirmed by three independent review roles): `smartService`'s `updateUser`
 * self-transition restarts an in-flight write rather than dropping the event,
 * but its invoke still reads the machine's `context` as it stood when the
 * state was (re-)entered — not the result of the write it is superseding. Two
 * favorite-star clicks fired back to back, without this, both compute
 * `nextFavorites` from the SAME pre-first-click list, and the second's
 * `updateUser` silently overwrites the first on the backend. Serializing
 * means the second click's computation only runs once the first has actually
 * committed (`waitForUserInfoIdle`), so it starts from the real post-first-
 * click list — at the cost of the second click's own write landing slightly
 * later, never of losing the first.
 */
export function toggleFavoriteOrg(orgId: string): void {
  pendingToggle = pendingToggle
    .then(() => waitForUserInfoIdle())
    .then(
      () =>
        new Promise<void>((resolve) => {
          const { userInfo, accountId } = smartService.state.context || {};

          if (!userInfo?.id || !accountId) {
            resolve();
            return;
          }

          const currentFavorites: string[] =
            userInfo.settings?.favoriteOrgIds || [];
          const nextFavorites = currentFavorites.includes(orgId)
            ? currentFavorites.filter((id) => id !== orgId)
            : [...currentFavorites, orgId];

          smartService.send({
            type: 'updateUser',
            userInfo: {
              id: userInfo.id,
              accountId,
              firstName: userInfo.firstName,
              lastName: userInfo.lastName,
              settings: {
                ...userInfo.settings,
                favoriteOrgIds: nextFavorites,
              },
            },
          });

          // The event was just sent above, so the machine is synchronously
          // already in `updating` (XState v4 `send` processes synchronously;
          // the invoke's `src` is always async, so it cannot have resolved
          // yet) — `waitForUserInfoIdle` here always takes the subscribe
          // branch, resolving once this specific write actually commits.
          waitForUserInfoIdle().then(resolve);
        })
    );
}

/**
 * Subscribes to changes in the logged-in user's favorite org ids, firing once
 * synchronously with the current value. Returns an unsubscribe function.
 */
export function subscribeToFavoriteOrgIds(
  callback: (favoriteOrgIds: string[]) => void
): () => void {
  callback(getFavoriteOrgIds());

  const subscription = smartService.subscribe(() => {
    callback(getFavoriteOrgIds());
  });

  return () => subscription.unsubscribe();
}
